import React, {useEffect} from "react";
import {IStorefrontLink, IStorefrontSettings} from "../../Api/Models/StorefrontSettings";
import { Image } from '@fluentui/react-components';
import MarkdownView from "react-showdown";
import {useNavigate} from "react-router-dom";
import {elementStyleFrom} from "../Helpers/searchPageConfig";
import { resolveHoverPart } from '../Helpers/stylesConfig';
import {parseFooterConfig} from "../Helpers/headerFooterConfig";
import {parseLayout} from "../Helpers/layout";
import LayoutRenderer from "../Layout/LayoutRenderer";
import {readableOn} from "../Helpers/ColorHelpers";
import {useLocalization} from "../Helpers/useLocalization";
import {useIsNarrow} from "../Helpers/useIsNarrow";
import {SOCIAL_GLYPHS} from "../Helpers/socialGlyphs";
interface IFooterProps {
    settings: IStorefrontSettings;
}

// Helper to group links by sections using separators
export const groupLinksBySections = (links: IStorefrontLink[]): IStorefrontLink[][] => {
    const sections: IStorefrontLink[][] = [];
    let currentSection: IStorefrontLink[] = [];
    links.forEach(link => {
        if (link.isSeparator) {
            if (currentSection.length > 0) {
                sections.push(currentSection);
                currentSection = [];
                currentSection.push(link);
            }
            else {
                currentSection.push(link);
            }
        } else {
            currentSection.push(link);
        }
    });
    if (currentSection.length > 0) {
        sections.push(currentSection);
    }
    return sections;
};

// Helper to split an array into n nearly equal groups
function splitIntoColumns<T>(arr: T[], n: number): T[][] {
    const result: T[][] = Array.from({ length: n }, () => []);
    arr.forEach((item, idx) => {
        result[idx % n].push(item);
    });
    return result;
}

const Footer: React.FC<IFooterProps> = (props) => {
    const whiteLabel = window.config.whiteLabel === 'true';
    const L = useLocalization();
    const [footerLinkChunks, setFooterLinkChunks] = React.useState<IStorefrontLink[][]>([]);
    const nav = useNavigate();

    // Merchant-configured footer chrome styling (footerConfig JSON).
    const footerConfig = React.useMemo(() => parseFooterConfig(props.settings.footerConfig), [props.settings.footerConfig]);
    const footerBarStyle = React.useMemo(() => elementStyleFrom(footerConfig.bar), [footerConfig.bar]);
    const footerLeftStyle = React.useMemo(() => elementStyleFrom(footerConfig.leftText), [footerConfig.leftText]);
    const isNarrow = useIsNarrow(700);
    const footerRightStyle = React.useMemo(() => elementStyleFrom(footerConfig.rightText), [footerConfig.rightText]);
    // The email/phone links follow the footer's own text color (rightText, else the footer bar), so a
    // footer bar re-colored darker than the header stays legible. Falls back to menuBarTextColor
    // (today's behavior) when the footer chrome sets no color of its own.
    const footerContactColor = (footerRightStyle.color as string | undefined)
        ?? (footerBarStyle.color as string | undefined)
        ?? props.settings.menuBarTextColor;
    const footerLinkHeaderStyle = React.useMemo(() => elementStyleFrom(footerConfig.linkHeader), [footerConfig.linkHeader]);
    const footerLinkStyle = React.useMemo(() => elementStyleFrom(footerConfig.link), [footerConfig.link]);
    // Optional bounding container around the three footer columns. Caps the columns' width and
    // auto-centers them. The border/paddingBottom are pulled OUT here and rendered as a dedicated
    // divider line just above the social row (see footerDivider below): the columns container is a
    // flex child that stretches to the full footer height, so a borderBottom on it would land at the
    // very bottom of the footer, under the socials — not between the columns and the socials.
    const footerInnerStyle = React.useMemo(() => {
        const s: React.CSSProperties = elementStyleFrom(footerConfig.contentContainer);
        delete s.borderBottom; delete s.paddingBottom;
        return s;
    }, [footerConfig.contentContainer]);
    const cc = footerConfig.contentContainer;
    const footerDividerBorder = cc?.borderBottom as string | undefined;
    // Optional circular badge around each social icon. When a backgroundColor is configured we
    // draw a round badge and whiten the (full-color PNG) glyph so it reads on the badge.
    const socialBadgeStyle = React.useMemo(() => elementStyleFrom(footerConfig.socialBadge), [footerConfig.socialBadge]);
    const hasSocialBadge = !!(socialBadgeStyle && (socialBadgeStyle.backgroundColor));
    const socialBadgeWrap: React.CSSProperties | undefined = hasSocialBadge
        ? { width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', ...socialBadgeStyle }
        : undefined;
    // Glyph color inside the badge (defaults to white so it reads on a brand-colored circle).
    const socialGlyphColor = (socialBadgeStyle?.color as string | undefined) ?? '#ffffff';
    // Optional hover color for the badge (e.g. shift to slate on hover). Applied via a scoped
    // <style> rule since inline styles can't express :hover.
    const socialBadgeHoverStyle = React.useMemo(() => elementStyleFrom(resolveHoverPart(footerConfig.socialBadgeHover, footerConfig.socialBadge), 'hover'), [footerConfig.socialBadgeHover, footerConfig.socialBadge]);
    const socialBadgeHoverBg = hasSocialBadge ? (socialBadgeHoverStyle?.backgroundColor as string | undefined) : undefined;
    const socialAlign = footerConfig.socialAlign ?? 'right';
    const socialRowPosition: React.CSSProperties = socialAlign === 'center'
        ? { left: 0, right: 0, justifyContent: 'center' }
        : socialAlign === 'left'
            ? { left: 24, justifyContent: 'flex-start' }
            : { right: 24, justifyContent: 'flex-end' };
    // The footer links/headers default to menuBarTextColor, but on a footer bar whose background
    // differs from the header's (a footerConfig.bar override, or simply a menuBarTextColor tuned for
    // the header) that can render invisible — e.g. the privacy-policy link vanishing. Fall back to a
    // luminance-aware readable color when menuBarTextColor lacks contrast with the footer bar; an
    // explicit footerConfig.link/linkHeader color still wins (spread after).
    const footerBarBg = (footerBarStyle.backgroundColor as string | undefined) ?? props.settings.menuBarColor;
    const footerTextColor = readableOn(footerBarBg, props.settings.menuBarTextColor);
    useEffect(() => {
        if (Array.isArray(props.settings.storefrontFooterLinks)) {
            setFooterLinkChunks(groupLinksBySections(props.settings.storefrontFooterLinks));
        }
    }, [props.settings.storefrontFooterLinks]);

    const goToFooterLink = () => {
        if(!props.settings.footerLogoLink) return;
        const link = props.settings.footerLogoLink ?? '/';
        if (link.startsWith('http')) {
            window.open(link, '_blank');
        }
        else{
            nav(link);
        }
    }
    
    
    const getLinkElement = (link: IStorefrontLink, index: number)=> {
        if(link.isSeparator){
            return (
                <div key={`footer-link-separator-${link.text}-${index}`}>
                {link.url ?
                    <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: footerTextColor,
                            textDecoration: 'none',
                            display: 'block',
                            marginBottom: 8,
                            fontSize: '1em',
                            ...footerLinkHeaderStyle
                        }}
                    >
                        <h2>{link.text}</h2>
                    </a> :
                    <div style={footerLinkHeaderStyle}>
                        <h2>{link.text}</h2>
                    </div>}
                </div>

            );
        }
        return (
            <div key={`footer-link-${link.text}-${index}`}>
                <a

                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        color: footerTextColor,
                        textDecoration: 'none',
                        display: 'block',
                        marginBottom: 8,
                        fontSize: '1em',
                        ...footerLinkStyle
                    }}
                >
                    <div style={{fontSize: '1em', margin: 0, fontWeight: 500}}>{link.text}</div>
                </a>
            </div>
        );
    }
    // AC-948 / S2.5 — footer layout gate: a valid, non-empty `layout` routes to the grid renderer;
    // otherwise fall through to the legacy footer below. Non-adopters are byte-identical (AC-935).
    const footerLayout = parseLayout(footerConfig.layout);
    if (footerLayout) {
        return (
            <div className='footer'>
                <div className='footer-content sf-layout-footer-bar' style={{ backgroundColor: props.settings.menuBarColor, color: props.settings.menuBarTextColor, fontFamily: props.settings.headerFont, ...footerBarStyle }}>
                    <LayoutRenderer
                        layout={footerLayout}
                        ctx={{ settings: props.settings, region: 'footer', nav: (url) => nav(url), isNarrow, L }}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={'footer'}>
            {props.settings?.includeFooter &&
                <div className={"footer-content"} style={{
                    backgroundColor: props.settings.menuBarColor,
                    color: props.settings.menuBarTextColor,
                    fontFamily: props.settings.headerFont,
                    height: '440px',
                    display: 'flex',
                    position: 'relative',
                    marginBottom: '40px',
                    paddingLeft: '10px',
                    paddingTop: '30px',
                    minWidth: '600px',
                    ...footerBarStyle,
                    // Narrow screens: the fixed 440px height + 600px min-width + absolute socials all
                    // break down, so switch to a natural-height single column that stacks.
                    ...(isNarrow ? { height: 'auto', minHeight: 'auto', minWidth: 0, flexDirection: 'column', paddingLeft: 16, paddingRight: 16, paddingBottom: 24 } : {})
                }}>
                    <div className={"footer-content-inner"} style={{ display: 'flex', flexDirection: 'row', width: '100%', margin: '0 auto', gap: 32, alignItems: 'flex-start', ...footerInnerStyle, ...(isNarrow ? { flexDirection: 'column', gap: 28, alignItems: 'stretch', maxWidth: 'none' } : {}) }}>
                    <div className={"footer-left"} style={{flex: '1 1 0', minWidth: 0, display: 'flex', paddingLeft: '20px', paddingTop: '20px', flexDirection: 'column', alignItems: 'flex-start', gap: 4, ...footerLeftStyle, ...(isNarrow ? { lineHeight: 1.3, flex: '0 0 auto', width: '100%', paddingLeft: 0, paddingTop: 0 } : {})}}>
                        {props.settings.footerLogo && <div className={'header-title'} style={{ display: 'flex', alignItems: 'center' }}>
                            {props.settings.footerLogo &&
                                <img src={props.settings.footerLogo} alt="Logo" style={{ height: '3em', cursor: 'pointer' }} onClick={goToFooterLink} />
                            }
                        </div>}
                        {props.settings.footerText && <div>
                            <MarkdownView markdown={props.settings.footerText}/>
                        </div>}
                    </div>
                    <div className={'footer-center'} style={{
                        flex: '1 1 0',
                        minWidth: 0,
                        display: 'flex',
                        gap: '24px',
                        ...(isNarrow ? { flex: '0 0 auto', width: '100%', flexDirection: 'column', gap: 4 } : {})
                    }}>
                        {splitIntoColumns(footerLinkChunks, 3).map((column, colIdx) => (
                            <div key={`footer-link-column-${colIdx}`} className={`footer-link-column-${colIdx}`} style={{ display: 'flex', flexDirection: 'column', breakInside: 'avoid', flex: '1 1 0', minWidth: 0 }}>
                                {column.map((section, sectionIdx) => (
                                    <React.Fragment key={`footer-link-chunk-${colIdx}-${sectionIdx}`}>
                                        {section.map((link, idx) => getLinkElement(link, sectionIdx))}
                                    </React.Fragment>
                                ))}
                            </div>
                        ))}
                    </div>
                    <div className={"footer-right"} style={{flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, ...footerRightStyle, ...(isNarrow ? { flex: '0 0 auto', width: '100%' } : {})}}>
                        {props.settings.footerContactDetails && <div>
                            <h2>{L('localization_footer_contact_heading')}</h2>
                            <MarkdownView markdown={props.settings.footerContactDetails}/>
                        </div>}
                        {props.settings.footerEmail && <div className={"footer-email"}><a href={`mailto:${props.settings.footerEmail}`} style={{color: footerContactColor}}>{props.settings.footerEmail}</a></div>}
                        {props.settings.footerPhone && <div className={"footer-phone"}><a href={`tel:${props.settings.footerPhone}`} style={{color: footerContactColor}}><h1>{props.settings.footerPhone}</h1></a></div>}
                    </div>
                    </div>
                    {footerDividerBorder && !isNarrow && <div className="footer-divider" style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 76,
                        margin: '0 auto',
                        maxWidth: cc?.maxWidth,
                        borderTop: footerDividerBorder,
                        boxSizing: 'border-box'
                    }} />}
                    <div className="footer-socials-row" style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: hasSocialBadge ? 12 : 5,
                        position: 'absolute',
                        bottom: 24,
                        ...socialRowPosition,
                        // Narrow: pull the socials into normal flow below the stacked columns, centered,
                        // with the divider as a top border so it still separates content from socials.
                        ...(isNarrow ? { position: 'static', left: 'auto', right: 'auto', width: '100%', justifyContent: 'center', marginTop: 24, paddingTop: 20, borderTop: footerDividerBorder || undefined } : {})
                    }}>
                        {socialBadgeHoverBg && <style>{`.footer .footer-social-badge:hover{background-color:${socialBadgeHoverBg} !important}`}</style>}
                        {[
                            { url: props.settings.footerFacebook, icon: 'facebook.png', alt: 'Facebook', scale: 0.65 },
                            // Twitter and X are distinct now (AC-971). Twitter renders the bird from the
                            // vector glyph (there's no bird PNG asset), so it has no `icon`; X uses x.png.
                            { url: props.settings.footerTwitter, icon: undefined as string | undefined, alt: 'Twitter', scale: 0.55 },
                            { url: props.settings.footerX, icon: 'x.png', alt: 'X', scale: 0.55 },
                            { url: props.settings.footerLinkedIn, icon: 'linkedin.png', alt: 'LinkedIn', scale: 0.65 },
                            { url: props.settings.footerYoutube, icon: 'youtube.png', alt: 'YouTube', scale: 1 },
                            { url: props.settings.footerBluesky, icon: 'bluesky.png', alt: 'Bluesky', scale: 1 },
                        ].filter(s => s.url).map(s => (
                            <div key={s.alt} className={hasSocialBadge ? 'footer-social-link-wrapper footer-social-badge' : 'footer-social-link-wrapper'} style={socialBadgeWrap} onClick={() => window.open(s.url, '_blank', 'noreferrer')}>
                                {/* Use the vector glyph in badge mode, or whenever there's no PNG asset for
                                    the social (e.g. the Twitter bird); otherwise the full-color PNG. */}
                                {(hasSocialBadge || !s.icon) && SOCIAL_GLYPHS[s.alt]
                                    ? <svg className="footer-social-link" width={20} height={20} viewBox="0 0 24 24" fill={hasSocialBadge ? socialGlyphColor : footerTextColor} aria-label={s.alt} role="img"><path d={SOCIAL_GLYPHS[s.alt]} /></svg>
                                    : <Image
                                        className="footer-social-link"
                                        src={`https://acapp1stousee0101.blob.core.windows.net/app-images/socials/${s.icon}`}
                                        alt={s.alt}
                                        fit='contain'
                                        style={{ width: 48, height: 48, cursor: 'pointer', transform: `scale(${s.scale})` }}
                                    />}
                            </div>
                        ))}
                    </div>
                    {whiteLabel ?
                        <div className={"powered-by-footer-absolute"}>
                            Powered by AdaptiveCatalog
                        </div>: <></>
                    }
                </div>
            }
        </div>
    )
}

export default Footer;